<template>
  <div class="summary-content" data-testid="summary-content">
    <!-- Processing State -->
    <div v-if="isProcessing" class="summary-content__processing">
      <div class="processing-indicator">
        <Icon name="ai.processing" size="sm" />
      </div>
      <span>{{ displayProcessingMessage }}</span>
    </div>

    <!-- Edit Mode (shown for both new and existing descriptions) -->
    <div v-else-if="editMode" class="summary-content__edit">
      <FormKit
        type="textarea"
        name="summary"
        id="summary-edit-input"
        data-testid="summary-edit-input"
        v-model="editModel"
        auto-height
        validation-visibility="live"
        @keydown.escape="exitEditMode"
        @keydown.enter.ctrl.exact="onSave"
        @keydown.enter.meta.exact="onSave"
      />
      <div class="summary-content__edit-actions">
        <div class="flex gap-2">
          <Button type="solid" variant="blue" icon="general.tick" text="Save" @click="onSave" data-testid="summary-save-btn" />
          <Button type="link" variant="black" text="Cancel" @click="exitEditMode" data-testid="summary-cancel-btn" />
        </div>
        <Button
          type="link"
          variant="black"
          href="https://support.zight.com/hc/en-us/articles/23315850688663"
          target="_blank"
          rel="noopener noreferrer"
          icon="general.textFields"
          text="Markdown is supported"
        />
      </div>
    </div>

    <!-- Empty State (owner can click to add description) -->
    <div
      v-else-if="!description?.length && isCurrentItemOwner"
      class="summary-content__placeholder"
      data-testid="summary-empty-placeholder"
      @click="enterEditMode"
    >
      <Icon name="general.alignLeft" size="base" class="text-zinc-400" />
      <span>Type a description</span>
    </div>

    <!-- Empty State (non-owner) -->
    <div v-else-if="!description?.length" class="summary-content__empty">
      <Icon name="general.alignLeft" size="base" class="text-zinc-400" />
      <span>No summary available</span>
    </div>

    <!-- Rendered markdown (hidden in edit mode) -->
    <div
      v-else
      class="summary-content__text leading-normal max-w-none prose-sm prose dark:text-zinc-300 prose-slate dark:prose-invert"
      :class="{ 'opacity-50': isProcessing }"
    >
      <MarkdownWithScreenshots data-testid="summary-content-text" :source="description" :linkify="true" :typographer="true" :breaks="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon, Button } from '@/storybook';
import MarkdownWithScreenshots from './MarkdownWithScreenshots.vue';

import { useViewerItemStore } from '../store/useViewerItemStore';
import { useAiMetadataStore } from '../store/useAiMetadataStore';
import { useSmartActionsStore } from '../store/useSmartActionsStore';
import { useTranscriptionStore } from '../store/useTranscriptionStore';
import { useAiProcessingMessage } from '../composables/useAiProcessingMessage';
import { useUser } from '../composables/useUser';
import { useToast } from '@/composables/useToast';
import { useConfirmModal } from '@/storybook/modal/useModal';
import { getNode } from '@formkit/core';

const store = useViewerItemStore();
const aiMetadataStore = useAiMetadataStore();
const smartActionsStore = useSmartActionsStore();
const transcriptionStore = useTranscriptionStore();
const { isCurrentItemOwner } = useUser();

const { description } = storeToRefs(store);
const { isProcessing: isAiMetadataProcessing } = storeToRefs(aiMetadataStore);
const { isProcessing: isSmartActionProcessing } = storeToRefs(smartActionsStore);
const { isTranscriptionProcessing } = storeToRefs(transcriptionStore);

const { error } = useToast();
const displayProcessingMessage = useAiProcessingMessage();

// Edit mode state
const editMode = ref(false);
const editModel = ref(description.value || '');

// Combined processing state
const isProcessing = computed(() => {
  return isAiMetadataProcessing.value || isSmartActionProcessing.value || isTranscriptionProcessing.value;
});

// Edit mode functions
const enterEditMode = () => {
  editModel.value = description.value || '';
  editMode.value = true;
};

const exitEditMode = async () => {
  const node = getNode('summary-edit-input');

  if (node && node.context?.state.dirty) {
    const { open } = useConfirmModal({
      title: 'Cancel & discard changes?',
      confirmText: 'Discard changes',
      cancelText: 'Back to editing',
    });

    const confirmed = await open();
    if (!confirmed) {
      return;
    }
  }

  editModel.value = description.value || '';
  editMode.value = false;
};

const onSave = async () => {
  if (editModel.value === description.value) {
    editMode.value = false;
    return;
  }

  try {
    await store.updateDescription(editModel.value);
    editMode.value = false;
  } catch {
    error('Cannot save summary');
  }
};

// Sync edit model when description changes externally
watch(description, () => {
  if (!editMode.value) {
    editModel.value = description.value || '';
  }
});

// Expose functions for parent components
defineExpose({
  enterEditMode,
  editMode,
});
</script>

<style scoped>
.summary-content {
  @apply flex flex-col gap-4;
}

.summary-content__processing {
  @apply flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300 py-4;
}

.processing-indicator {
  @apply w-6 h-6 p-1 flex items-center justify-center rounded-lg;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%), linear-gradient(90deg, #f5d9b7 0%, #e0adff 50%, #f5d9b7 100%);
  background-size: 200% 200%;
  animation: animateGlow 1.25s linear infinite;
}

.dark .processing-indicator {
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%),
    linear-gradient(90deg, rgba(245, 217, 183, 0.4) 0%, rgba(224, 173, 255, 0.5) 50%, rgba(245, 217, 183, 0.4) 100%);
  background-size: 200% 200%;
}

.summary-content__placeholder {
  @apply flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-500 py-4 cursor-text hover:text-zinc-500 dark:hover:text-zinc-400 transition-colors;
}

.summary-content__empty {
  @apply flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 py-4;
}

.summary-content__text {
  @apply flex-grow;
}

.summary-content__text :deep(.markdown-with-screenshots hr) {
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
}

.summary-content__text :deep(.markdown-with-screenshots h1),
.summary-content__text :deep(.markdown-with-screenshots h2),
.summary-content__text :deep(.markdown-with-screenshots h3),
.summary-content__text :deep(.markdown-with-screenshots h4),
.summary-content__text :deep(.markdown-with-screenshots h5),
.summary-content__text :deep(.markdown-with-screenshots h6) {
  margin-bottom: 0.125rem;
}

.summary-content__text :deep(.markdown-with-screenshots hr + h1),
.summary-content__text :deep(.markdown-with-screenshots hr + h2),
.summary-content__text :deep(.markdown-with-screenshots hr + h3),
.summary-content__text :deep(.markdown-with-screenshots hr + h4),
.summary-content__text :deep(.markdown-with-screenshots hr + h5),
.summary-content__text :deep(.markdown-with-screenshots hr + h6) {
  margin-top: 0;
}

.summary-content__text :deep(.markdown-with-screenshots p) {
  margin-top: 0;
  margin-bottom: 0.25rem;
}

.summary-content__text :deep(.markdown-with-screenshots ul),
.summary-content__text :deep(.markdown-with-screenshots ol) {
  margin-top: 0.125rem;
}

.summary-content__text :deep(.markdown-with-screenshots p + ul),
.summary-content__text :deep(.markdown-with-screenshots p + ol) {
  margin-top: 0;
}

.summary-content__text :deep(.markdown-with-screenshots li) {
  margin-bottom: 0.125rem;
}

.summary-content__text :deep(.markdown-with-screenshots blockquote) {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.summary-content__edit {
  @apply flex flex-col gap-2;
}

.summary-content__edit-actions {
  @apply flex justify-between items-center;
}

@keyframes animateGlow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}
</style>
